import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
// @ts-ignore
import { useIntl } from 'react-intl'; // TODO: Replace react-intl with intl-next for hook usage
import config from 'appconfig';
import { State } from 'model/State';
import { Dimensions } from 'components/visuals';
import t from 'modules/i18n/intl';
import RedirectToProductList from './components/redirectToProductList';
import Table from 'components/common/Table/Table';
import TableContent, { MACTableType } from './table/TableContent';
import { OrderResult } from './duck/models/orders';
import Spinner from '@atlaskit/spinner';
import { fetchQuotesPost, ROUTE as ordersRouteType } from './duck/actions/orders';
import { ROUTE as ordersPreselectedRouteType } from './duck/actions/ordersPreselected';
import { ROUTE as invoicesRouteType } from './duck/actions/invoices';
import { ROUTE as invoicesPreselectedRouteType } from './duck/actions/invoicesPreselected';
import OrderSearch from './components/OrderSearch';
import { getUserChannel } from '../auth/selectors';
import { selectQuotes } from './duck/selectors';
import Pagination from '../../components/common/Pagination';
import OrderLookup from './components/OrderLookup';
import UpdatePONumberModal from './components/UpdatePONumberModal';
import {
  HeaderContainer,
  LinkButton,
  LinksContainer,
  PageTitle,
} from './components/styledComponents';
import MACPageHeader from './components/macPageHeader';
import ServerLicensesMessage from './components/ServerLicensesMessage';

const LoadingContainer = styled.td`
  padding-top: ${Dimensions.units(7)}px;
  height: 450px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const TabsContainer = styled(LinksContainer)`
  margin-bottom: ${Dimensions.units(3.25)}px;
`;

const PaginationWrapper = styled.div`
  margin-top: 10px;
`;

const orderCenterLinks = [
  {
    text: t('mac.orders.menu.pricing'),
    href: config.wacPurchaseLicensingUrl,
    key: 'pricing',
  },
  {
    text: t('mac.orders.menu.pay-quote'),
    href: '/purchase/my/payment',
    key: 'quote',
  },
  {
    text: t('mac.orders.menu.new-order'),
    // TODO: Update new order link here in scope of CHT-2589
    href: '/',
    key: 'new-order',
  },
  {
    text: t('mac.orders.menu.questions'),
    href: config.companyContact,
    key: 'questions',
  },
];

const tableHeadersOrders = [
  {
    display: 'Order #',
    key: 'orders-sen-number',
    width: 15,
  },
  {
    display: 'Created',
    key: 'orders-created',
    width: 15,
  },
  {
    display: 'Total',
    key: 'orders-total',
    width: 15,
  },
  {
    display: 'Status',
    key: 'orders-status',
    width: 15,
  },
  {
    display: 'Actions',
    key: 'orders-actions',
    width: 40,
  },
];

const activeTabs = [
  {
    tableType: MACTableType.orders,
    label: t('mac.orders.tabs.quotes'),
    href: `${config.root}/my/orders`,
  },
  {
    tableType: MACTableType.invoices,
    label: t('mac.orders.tabs.invoices'),
    href: `${config.root}/my/invoices`,
  },
];

const Loading = () => (
  <tr>
    <LoadingContainer colSpan={5}>
      <SpinnerWrapper>
        <Spinner size="large" />
      </SpinnerWrapper>
    </LoadingContainer>
  </tr>
);

const getActiveRouteType = (routeType: string): MACTableType => {
  switch (routeType) {
    case ordersRouteType:
    case ordersPreselectedRouteType:
      return MACTableType.orders;
    case invoicesRouteType:
    case invoicesPreselectedRouteType:
      return MACTableType.invoices;

    default:
      return MACTableType.orders;
  }
};

export type OrderCenterPaginationProps = { maxResult: number; offset: number; totalItems: number };

export type OrderCenterTableItems = OrderResult[] | null;

export interface OrdersPageProps {
  orderId?: string;
  items: OrderCenterTableItems;
  pagination: OrderCenterPaginationProps;
  onPaginationChange: (
    params: Partial<Omit<OrderCenterPaginationProps, 'totalItems' | 'maxResult'>>,
  ) => void;
}

export const OrdersPage = ({ orderId, items, pagination, onPaginationChange }: OrdersPageProps) => {
  const intl = useIntl();
  const routeType = useSelector((state) => (state as State).location.type);
  const activeTableType = getActiveRouteType(routeType);
  const isPartner: boolean = useSelector(
    (state) =>
      getUserChannel(state as State).ifSuccess((userChannel) => userChannel.isPartner) || false,
  );

  useEffect(() => {
    document.title = intl.formatMessage({ id: 'document.title.mac.orders' });
  }, []);

  const shouldShowPagination = pagination?.maxResult < pagination?.totalItems;

  return (
    <>
      <RedirectToProductList />
      <MACPageHeader />
      <ServerLicensesMessage />
      <HeaderContainer bottomLine>
        <PageTitle> {t('mac.orders.title')} </PageTitle>
        <div data-cy="mac-orders-links">
          {orderCenterLinks.map((link) => (
            <LinkButton appearance="link" href={link.href} key={link.key}>
              {link.text}
            </LinkButton>
          ))}
        </div>
      </HeaderContainer>

      <TabsContainer>
        <div>
          {activeTabs.map((tab) => (
            <LinkButton
              key={tab.tableType}
              href={tab.href}
              appearance={tab.tableType === activeTableType ? 'link' : 'subtle-link'}
            >
              {tab.label}
            </LinkButton>
          ))}
        </div>
        <OrderSearch tableType={activeTableType} />
      </TabsContainer>

      <Table
        body={
          items ? (
            <TableContent
              items={items}
              tableType={activeTableType}
              isPartner={isPartner}
              senNumber={orderId}
            />
          ) : (
            <Loading />
          )
        }
        footers={[]}
        headers={tableHeadersOrders}
        dataAttr={activeTableType}
      />
      {shouldShowPagination && (
        <PaginationWrapper data-cy="pagination">
          <Pagination
            limit={pagination.maxResult}
            offset={pagination.offset}
            totalItems={pagination.totalItems}
            onPaginationChange={onPaginationChange}
          />
        </PaginationWrapper>
      )}

      <OrderLookup isPartner={isPartner} />
      <UpdatePONumberModal />
    </>
  );
};

export const MACOrdersPage = (props: Pick<OrdersPageProps, 'orderId'>) => {
  const dispatch = useDispatch();
  const [pagination, setPagination] = useState<OrderCenterPaginationProps>({
    maxResult: 10,
    offset: 0,
    totalItems: 10,
  });

  const orders = useSelector(
    (state) => selectQuotes(state as State)?.results || null,
  ) as OrderCenterTableItems;

  const totalResults = useSelector((state) => selectQuotes(state as State)?.totalAvailable || 0);

  useEffect(() => {
    if (totalResults > 0) {
      setPagination({
        ...pagination,
        totalItems: totalResults,
      });
    }
  }, [totalResults]);

  const handlePaginationChange = (params: { offset?: number }) => {
    setPagination({
      ...pagination,
      offset: params.offset || 0,
    });

    dispatch(fetchQuotesPost(pagination.maxResult, params.offset || 0));
  };

  return (
    <OrdersPage
      items={orders}
      pagination={pagination}
      onPaginationChange={handlePaginationChange}
      {...props}
    />
  );
};
